/**
 * SDR Stock Manager - Date & Expiry Utilities
 * @module utils/date
 */

/**
 * Left-pad a number or string with zero to 2 digits.
 * @param {number|string} value
 * @returns {string}
 */
export function padDatePart(value) {
  return String(value).padStart(2, '0');
}

/**
 * Normalizes year representations:
 * - Buddhist Era (BE > 2400) to Christian Era (CE = BE - 543)
 * - 2-digit years (< 100) to 2000s (e.g. 26 -> 2026)
 * @param {number|string} year
 * @returns {number} Normalized CE year or NaN
 */
export function normalizeYear(year) {
  const fullYear = Number(year);
  if (!Number.isFinite(fullYear)) return NaN;
  if (fullYear > 2400) return fullYear - 543;
  if (fullYear < 100 && fullYear >= 0) return fullYear + 2000;
  return fullYear;
}

/**
 * Builds a local Date at 00:00:00.000 safely validating day/month/year bounds.
 * @param {number|string} year
 * @param {number|string} month - 1-indexed (1 = Jan, 12 = Dec)
 * @param {number|string} day - 1-31
 * @returns {Date|null}
 */
export function buildLocalDate(year, month, day) {
  const y = normalizeYear(year);
  const m = Number(month);
  const d = Number(day);
  if (!Number.isFinite(y) || !Number.isFinite(m) || !Number.isFinite(d)) return null;
  if (m < 1 || m > 12 || d < 1 || d > 31) return null;

  const date = new Date(y, m - 1, d);
  date.setHours(0, 0, 0, 0);
  if (date.getFullYear() !== y || date.getMonth() !== m - 1 || date.getDate() !== d) {
    return null;
  }
  return date;
}

/**
 * Formats a Date object to YYYY-MM-DD input string in local calendar.
 * @param {Date} date
 * @returns {string}
 */
export function dateToInputValue(date) {
  if (!(date instanceof Date) || isNaN(date.getTime())) return '';
  return `${date.getFullYear()}-${padDatePart(date.getMonth() + 1)}-${padDatePart(date.getDate())}`;
}

/**
 * Safely parses any date representation (ISO, slash format, BE year, Excel serial, Date)
 * into a normalized local calendar Date object at 00:00:00.000.
 * @param {Date|number|string|null|undefined} dateValue
 * @returns {Date|null}
 */
export function safeParseDate(dateValue) {
  if (dateValue === null || dateValue === undefined || dateValue === '') return null;

  if (dateValue instanceof Date) {
    if (isNaN(dateValue.getTime())) return null;
    return buildLocalDate(dateValue.getFullYear(), dateValue.getMonth() + 1, dateValue.getDate());
  }

  if (typeof dateValue === 'number' && Number.isFinite(dateValue)) {
    // Excel serial dates typically range from 20000 (1954) to 80000 (2119)
    if (dateValue > 20000 && dateValue < 80000) {
      const utc = new Date(Date.UTC(1899, 11, 30) + Math.round(dateValue) * 86400000);
      return buildLocalDate(utc.getUTCFullYear(), utc.getUTCMonth() + 1, utc.getUTCDate());
    }
    const parsedNumberDate = new Date(dateValue);
    if (!isNaN(parsedNumberDate.getTime())) {
      return buildLocalDate(parsedNumberDate.getFullYear(), parsedNumberDate.getMonth() + 1, parsedNumberDate.getDate());
    }
    return null;
  }

  if (typeof dateValue === 'string') {
    const value = dateValue.trim();
    if (!value) return null;

    // ISO format: YYYY-MM-DD (or YYYY-M-D)
    const isoMatch = value.match(/^(\d{4})-(\d{1,2})-(\d{1,2})/);
    if (isoMatch) {
      return buildLocalDate(isoMatch[1], isoMatch[2], isoMatch[3]);
    }

    // Slash format with year first: YYYY/MM/DD or YYYY.MM.DD
    const slashYearFirstMatch = value.match(/^(\d{4})[\/.](\d{1,2})[\/.](\d{1,2})$/);
    if (slashYearFirstMatch) {
      return buildLocalDate(slashYearFirstMatch[1], slashYearFirstMatch[2], slashYearFirstMatch[3]);
    }

    // Slash format with day first: DD/MM/YYYY or DD.MM.YYYY
    const slashMatch = value.match(/^(\d{1,2})[\/.](\d{1,2})[\/.](\d{2,4})$/);
    if (slashMatch) {
      return buildLocalDate(slashMatch[3], slashMatch[2], slashMatch[1]);
    }

    const parsed = new Date(value);
    if (!isNaN(parsed.getTime())) {
      return buildLocalDate(parsed.getFullYear(), parsed.getMonth() + 1, parsed.getDate());
    }
  }

  return null;
}

/**
 * Normalizes any date value to standard HTML input date string YYYY-MM-DD.
 * @param {Date|number|string|null|undefined} dateValue
 * @returns {string}
 */
export function normalizeDateInputValue(dateValue) {
  const date = safeParseDate(dateValue);
  return date ? dateToInputValue(date) : '';
}

/**
 * Computes calendar days remaining until expiry date relative to reference date.
 * @param {string|Date|null|undefined} expiryDateStr
 * @param {Date} [referenceDate=new Date()]
 * @returns {number|null} Days until expiry (>0: future, 0: today, <0: past), or null if no expiry.
 */
export function getDaysUntilExpiry(expiryDateStr, referenceDate = new Date()) {
  if (!expiryDateStr) return null;
  const expiry = safeParseDate(expiryDateStr);
  if (!expiry || isNaN(expiry.getTime())) return null;

  const today = safeParseDate(referenceDate) || new Date();
  today.setHours(0, 0, 0, 0);
  expiry.setHours(0, 0, 0, 0);

  const diffTime = expiry.getTime() - today.getTime();
  return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
}

/**
 * Formats an expiry date string to Thai localized format with CE (ค.ศ.) year (e.g. 15 ม.ค. 2026).
 * @param {string|Date|null|undefined} expiryDateStr
 * @param {string} [locale='th-TH']
 * @returns {string}
 */
export function formatExpiryDate(expiryDateStr, locale = 'th-TH') {
  if (!expiryDateStr) return '';
  const date = safeParseDate(expiryDateStr);
  if (!date || isNaN(date.getTime())) return String(expiryDateStr || '');

  return date.toLocaleDateString(locale, {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    calendar: 'gregory'
  });
}

/**
 * Formats a Date or date string to full Thai date string with CE year.
 * @param {Date|string} dateValue
 * @param {Intl.DateTimeFormatOptions} [options]
 * @returns {string}
 */
export function toThaiDateString(dateValue, options = {}) {
  const date = safeParseDate(dateValue) || (dateValue instanceof Date ? dateValue : new Date());
  if (isNaN(date.getTime())) return '';

  const defaultOptions = {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
    calendar: 'gregory',
    ...options
  };

  return date.toLocaleDateString('th-TH', defaultOptions);
}

/**
 * Determines expiry status, CSS badge class, and formatted label.
 * @param {string|Date|null|undefined} expiryDateStr
 * @param {string|Date|null|undefined} [warningDateStr=''] - Expiry threshold date from settings
 * @param {Date} [referenceDate=new Date()]
 * @returns {import('../types/index.js').DateExpiryStatus}
 */
export function getExpiryStatus(expiryDateStr, warningDateStr = '', referenceDate = new Date()) {
  if (!expiryDateStr) {
    return { class: '', text: 'ไม่มีวันหมดอายุ', code: 'none' };
  }

  const days = getDaysUntilExpiry(expiryDateStr, referenceDate);
  const formattedDate = formatExpiryDate(expiryDateStr);

  if (days === null) {
    return { class: '', text: 'ไม่มีวันหมดอายุ', code: 'none' };
  }

  if (days <= 0) {
    return {
      class: 'badge-danger',
      text: `หมดอายุแล้ว (${formattedDate})`,
      code: 'expired',
      days
    };
  }

  let isNearExpiry = false;
  if (warningDateStr) {
    const warningDate = safeParseDate(warningDateStr);
    const expiry = safeParseDate(expiryDateStr);
    if (warningDate && !isNaN(warningDate.getTime()) && expiry && !isNaN(expiry.getTime())) {
      warningDate.setHours(0, 0, 0, 0);
      expiry.setHours(0, 0, 0, 0);
      isNearExpiry = expiry.getTime() <= warningDate.getTime();
    }
  }

  if (isNearExpiry) {
    const months = Math.max(0, Math.ceil(days / 30));
    return {
      class: 'badge-warning',
      text: `ใกล้หมดอายุ (${formattedDate} - ${months} ด.)`,
      code: 'near-expiry',
      days
    };
  }

  return {
    class: 'badge-success',
    text: formattedDate,
    code: 'safe',
    days
  };
}

/**
 * Checks if an item is already expired.
 * @param {string|Date|null|undefined} expiryDateStr
 * @param {Date} [referenceDate=new Date()]
 * @returns {boolean}
 */
export function isExpired(expiryDateStr, referenceDate = new Date()) {
  const days = getDaysUntilExpiry(expiryDateStr, referenceDate);
  return days !== null && days <= 0;
}

/**
 * Checks if an item is near expiry (or expired).
 * @param {string|Date|null|undefined} expiryDateStr
 * @param {string|Date|null|undefined} [warningDateStr='']
 * @param {Date} [referenceDate=new Date()]
 * @returns {boolean}
 */
export function isExpiringSoon(expiryDateStr, warningDateStr = '', referenceDate = new Date()) {
  const status = getExpiryStatus(expiryDateStr, warningDateStr, referenceDate);
  return status.code === 'near-expiry' || status.code === 'expired';
}

/**
 * Formats date and time into a concise Thai string for transactions and audits.
 * @param {string|Date|number} dateValue
 * @returns {string}
 */
export function formatDateThai(dateValue) {
  if (!dateValue) return '-';
  try {
    const d = new Date(dateValue);
    if (isNaN(d.getTime())) return String(dateValue);
    return d.toLocaleDateString('th-TH', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  } catch (e) {
    return String(dateValue || '-');
  }
}
